<div class="offer-rules-wrapper">
  <div class="header-row">
    <div class="header-copy">
      <h3>Offer Set Rules Configuration</h3>
      <p>Manage offer set rules and dollar discount rules for non-VIN offer sets. Use the table to review and edit rules.</p>
    </div>
    <div class="header-actions">
      <p-button
        label="Process Rules"
        severity="secondary"
        [disabled]="isProcessRulesButtonDisabled"
        [loading]="isProcessingRules"
        (click)="requestProcessRules()">
      </p-button>
      <p-button
        label="New Rule"
        icon="pi pi-plus"
        severity="success"
        [disabled]="getRuleActionsDisabled()"
        (click)="openCreateDialog('offerSetRule')">
      </p-button>
    </div>
  </div>

  @if (isOfferRegistryProcessingActive) {
    <p-message
      severity="info"
      text="Rule processing is currently running. Rule controls are disabled until processing completes.">
    </p-message>
  }

  <p-card class="table-card">
    <p-toolbar>
      <ng-template pTemplate="left">
        <div class="toolbar-left">
          <span class="search-field">
            <p-iconField iconPosition="left">
              <p-inputIcon class="pi pi-search"></p-inputIcon>
              <input pInputText type="text" [(ngModel)]="searchValue" (input)="onSearch(searchValue)" placeholder="Search rules" />
            </p-iconField>
          </span>
        </div>
      </ng-template>
      <ng-template pTemplate="right">
        <div class="toolbar-right">
          <p-selectButton
            [options]="ruleTypeFilterOptions"
            [(ngModel)]="selectedRuleTypeFilter"
            (onChange)="onRuleTypeFilterChange($event.value)"
            optionLabel="label"
            optionValue="value">
          </p-selectButton>
        </div>
      </ng-template>
    </p-toolbar>

    <div class="table-section">
      <h4>Public Rules</h4>
      <p-table [value]="filteredPublicRules" [scrollable]="true" [loading]="tableLoading" dataKey="id" class="custom-table">
        <ng-template pTemplate="header">
          <tr>
            <th>Rule</th>
            <th>Status</th>
            <th>Vehicle Criteria</th>
            <th>Max Price</th>
            <th>Last Update</th>
            <th>Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-rule>
          <tr>
            <td>
              <div class="rule-name">
                @if (rule.offerSetId) {
                  <a [href]="getOfferSetLink(rule)" target="_blank" rel="noopener noreferrer">
                    {{ getOfferSetName(rule) }}
                  </a>
                } @else {
                  <span class="discount-label">{{ getOfferSetName(rule) }}</span>
                }
              </div>
              @if (rule.offerSetId && offerSetOfferTextMap[rule.offerSetId]) {
                <div class="offer-text">
                  {{ offerSetOfferTextMap[rule.offerSetId] }}
                </div>
              }
            </td>
            <td>
              <div class="status-indicator">
                <span class="status-dot" [ngClass]="getRuleStatusClass(rule)"></span>
                <span class="status-label">{{ getRuleStatusLabel(rule) }}</span>
              </div>
            </td>
            <td>
              <div class="criteria-tags">
                <p-tag *ngFor="let tag of getVehicleCriteriaTags(rule)" [value]="tag"></p-tag>
              </div>
            </td>
            <td>
              @if (rule.maxSalePrice !== null && rule.maxSalePrice !== undefined) {
                <span>{{ rule.maxSalePrice | currency:'USD':'symbol':'1.0-0' }}</span>
              } @else {
                —
              }
            </td>
            <td>
              @if (getLatestTimestampDisplay(rule); as latestText) {
                <div class="timestamp">{{ latestText }}</div>
              }
            </td>
            <td class="actions-cell">
              <div class="action-buttons">
                <p-button icon="pi pi-pencil" severity="info" [disabled]="getRuleActionsDisabled()" (click)="openEditDialog(rule)"></p-button>
                <p-button icon="pi pi-trash" severity="danger" [disabled]="getRuleActionsDisabled()" (click)="deleteRule(rule)"></p-button>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6" class="empty">No rules found.</td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <div class="table-section">
      <h4>Private Rules</h4>
      <p-table [value]="filteredPrivateRules" [scrollable]="true" [loading]="tableLoading" dataKey="id" class="custom-table">
        <ng-template pTemplate="header">
          <tr>
            <th>Rule</th>
            <th>Status</th>
            <th>Vehicle Criteria</th>
            <th>Max Price</th>
            <th>Discount Amount</th>
            <th>Last Update</th>
            <th>Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-rule>
          <tr>
            <td>
              <div class="rule-name">
                @if (rule.offerSetId) {
                  <a [href]="getOfferSetLink(rule)" target="_blank" rel="noopener noreferrer">
                    {{ getOfferSetName(rule) }}
                  </a>
                } @else {
                  <span class="discount-label">{{ getOfferSetName(rule) }}</span>
                }
              </div>
              @if (rule.offerSetId && offerSetOfferTextMap[rule.offerSetId]) {
                <div class="offer-text">
                  {{ offerSetOfferTextMap[rule.offerSetId] }}
                </div>
              }
            </td>
            <td>
              <div class="status-indicator">
                <span class="status-dot" [ngClass]="getRuleStatusClass(rule)"></span>
                <span class="status-label">{{ getRuleStatusLabel(rule) }}</span>
              </div>
            </td>
            <td>
              <div class="criteria-tags">
                <p-tag *ngFor="let tag of getVehicleCriteriaTags(rule)" [value]="tag"></p-tag>
              </div>
            </td>
            <td>
              @if (rule.maxSalePrice !== null && rule.maxSalePrice !== undefined) {
                <span>{{ rule.maxSalePrice | currency:'USD':'symbol':'1.0-0' }}</span>
              } @else {
                —
              }
            </td>
            <td>
              @if (rule.dollarAmountDiscount !== null && rule.dollarAmountDiscount !== undefined) {
                <span>{{ rule.dollarAmountDiscount | currency:'USD':'symbol':'1.0-0' }}</span>
              } @else {
                —
              }
            </td>
            <td>
              @if (getLatestTimestampDisplay(rule); as latestText) {
                <div class="timestamp">{{ latestText }}</div>
              }
            </td>
            <td class="actions-cell">
              <div class="action-buttons">
                <p-button icon="pi pi-pencil" severity="info" [disabled]="getRuleActionsDisabled()" (click)="openEditDialog(rule)"></p-button>
                <p-button icon="pi pi-trash" severity="danger" [disabled]="getRuleActionsDisabled()" (click)="deleteRule(rule)"></p-button>
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="7" class="empty">No rules found.</td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </p-card>

  <auto-offer-set-rule-modal
    [visible]="dialogVisible"
    [dialogTitle]="dialogTitle"
  [privateOffersEnabled]="privateOffersEnabled"
    [mode]="dialogMode"
    [rule]="selectedRule"
    [accountId]="accountId"
    [selectedRuleType]="dialogRuleType"
    [eligibleOfferSets]="eligibleOfferSets"
    [offerSetOfferTextMap]="offerSetOfferTextMap"
    [isProcessingDisabled]="getRuleActionsDisabled()"
    (closed)="handleDialogClosed()"
    (saved)="handleDialogSaved()">
  </auto-offer-set-rule-modal>

  <p-confirmDialog></p-confirmDialog>
</div>
